import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from './ui/Dialog';
import { Button } from './ui/Button';
import { History } from 'lucide-react';
import { useState, useEffect } from 'react';
import { apiClient } from '../lib/api-client';
import { Skeleton } from './ui/Skeleton';
import { ResponsiveSelect } from './ui/ResponsiveSelect';
import { Badge } from './ui/Badge';

interface UptimeHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  tenantCode: string;
  tenantName: string;
  environment: string;
  source: string;
}

type Incident = {
  source: string;
  severity: 'alert' | 'alarm';
  tenantCode: string;
  environment?: string;
  serviceId: string;
  serviceName?: string;
  monitorId: string;
  title: string;
  startedAt: string;
  endedAt?: string;
  samples: number;
  description?: string;
};

export function UptimeHistoryDialog({ open, onClose, tenantCode, tenantName, environment, source }: UptimeHistoryDialogProps) {
  const currentDate = new Date();
  const [year, setYear] = useState(currentDate.getFullYear());
  const [month, setMonth] = useState(currentDate.getMonth() + 1);
  const [activeTab, setActiveTab] = useState<'alarms' | 'alerts'>('alarms');
  const [loading, setLoading] = useState(true);
  const [alarms, setAlarms] = useState<Incident[]>([]);
  const [alerts, setAlerts] = useState<Incident[]>([]);

  const monthString = `${year}-${String(month).padStart(2, '0')}`;

  useEffect(() => {
    if (!open) {
      setLoading(true);
      setAlarms([]);
      setAlerts([]);
      setActiveTab('alarms');
      return;
    }

    const fetchHistory = async () => {
      setLoading(true);
      try {
        if (activeTab === 'alarms') {
          const res = await apiClient.getMonitoringAlarms(tenantCode);
          const filtered = (res?.alarms || []).filter((i: Incident) => 
            i.source === source && 
            i.environment === environment &&
            i.endedAt &&
            i.endedAt.startsWith(monthString)
          );
          setAlarms(filtered);
        } else {
          const res = await apiClient.getMonitoringAlerts(tenantCode);
          const filtered = (res?.alerts || []).filter((i: Incident) => 
            i.source === source && 
            i.environment === environment &&
            i.endedAt &&
            i.endedAt.startsWith(monthString)
          );
          setAlerts(filtered);
        }
      } catch (e) {
        console.error('Failed to fetch history:', e);
      } finally {
        setLoading(false);
      }
    };

    void fetchHistory();
  }, [open, tenantCode, environment, source, activeTab, monthString]);

  if (!open) return null;

  const yearOptions = [];
  for (let y = currentDate.getFullYear() - 2; y <= currentDate.getFullYear(); y++) {
    yearOptions.push({ value: String(y), label: String(y) });
  }

  const monthOptions = [
    { value: '1', label: 'January' }, { value: '2', label: 'February' }, { value: '3', label: 'March' },
    { value: '4', label: 'April' }, { value: '5', label: 'May' }, { value: '6', label: 'June' },
    { value: '7', label: 'July' }, { value: '8', label: 'August' }, { value: '9', label: 'September' },
    { value: '10', label: 'October' }, { value: '11', label: 'November' }, { value: '12', label: 'December' },
  ];

  const incidents = activeTab === 'alarms' ? alarms : alerts;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>
            <div className="flex items-center gap-2">
              <History className="w-5 h-5" />
              {source.charAt(0).toUpperCase() + source.slice(1)} History - {tenantName} ({environment})
            </div>
          </DialogTitle>
          <div className="flex flex-col sm:flex-row gap-2 mt-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Period</span>
              <ResponsiveSelect value={String(year)} onChange={(v) => setYear(Number(v))} options={yearOptions} className="w-24" />
              <ResponsiveSelect value={String(month)} onChange={(v) => setMonth(Number(v))} options={monthOptions} className="w-32" />
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant={activeTab === 'alarms' ? 'default' : 'outline'}
                onClick={() => setActiveTab('alarms')}
              >
                Alarms
              </Button>
              <Button
                size="sm"
                variant={activeTab === 'alerts' ? 'default' : 'outline'}
                onClick={() => setActiveTab('alerts')}
              >
                Alerts
              </Button>
            </div>
          </div>
        </DialogHeader>
        <DialogBody className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-24 w-full" />
              ))}
            </div>
          ) : incidents.length === 0 ? (
            <div className="py-12 text-center text-gray-500 dark:text-gray-400">
              No {activeTab} found for {monthString}
            </div>
          ) : (
            <div className="space-y-3">
              {incidents.map((i, idx) => {
                const isAlarm = i.severity === 'alarm';
                const borderColor = isAlarm ? 'border-red-500 bg-red-50 dark:bg-red-900/10' : 'border-orange-500 bg-orange-50 dark:bg-orange-900/10';
                const textColor = isAlarm ? 'text-red-700 dark:text-red-300' : 'text-orange-700 dark:text-orange-300';
                
                return (
                  <div key={idx} className={`rounded-lg border-l-4 ${borderColor} p-3`}>
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className={`text-sm font-bold ${textColor} uppercase`}>{i.severity}</div>
                      <Badge variant="outline" className="text-xs">{source === 'uptime' ? 'Uptime' : 'Alertmanager'}</Badge>
                    </div>
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{i.title}</div>
                    {i.description && (
                      <div className="text-xs text-gray-700 dark:text-gray-300 mt-1 italic">{i.description}</div>
                    )}
                    <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      <span>{i.serviceName || i.serviceId}</span>
                      <span className="mx-1">·</span>
                      <span>Started: {new Date(i.startedAt).toLocaleString()}</span>
                      {i.endedAt && (
                        <>
                          <span className="mx-1">·</span>
                          <span>Ended: {new Date(i.endedAt).toLocaleString()}</span>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogBody>
        <DialogFooter className="flex-shrink-0">
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

